import type { ObjectId } from 'mongodb';
import type { Document } from 'mongodb';

import type { AccountMilestoneStatus } from './AccountMilestones';
import type { AccountRequirementStatus } from './AccountRequirements';
import type { CurrentPaymentMethod, VerificationDaysTrigger, PlacedInServiceTrigger } from './AccountTypes';
import type { ApplicationApplicant, ApplicationMonthlyPaymentCap } from './Application';
import type { FundType, TrancheStatus } from './AssetTypes';
import type {
  ChangeOrderChangedField,
  ChangeOrderInitiatedBy,
  ChangeOrderReasonCode,
  ChangeOrderStatus,
} from './ChangeOrders';
import type { ContractStatus } from './Contracts';
import type { AllDocumentTypes, DocumentStatus } from './DocumentTypes';
import type { PAYMENT_TYPE } from './FinancingData';
import type { IsoDateTimeString } from './Helpers';
import type { PaymentMethod } from './InboundPayments';
import type { MilestoneRequirement, MilestoneType } from './Milestones';
import type { MaintenancePeriodUnit, OrganizationMaintenanceSettingsV2Value } from './OrganizationSettingsV2';
import type { ProgramType } from './ProgramTypes';
import type { QueueStatus, QueueType } from './Queues';
import type { QuoteStatus } from './Quotes';
import type { AccountStipulationReason, AccountStipulationType, StipulationType } from './Stipulations';
import type { TaskDocument } from './Tasks';

import type { FinancingDecision } from './index';

export enum WebhookEventType {
  accountCreated = 'accountCreated',
  accountUpdated = 'accountUpdated',
  activeQuoteExceedsMonthlyPaymentCaps = 'activeQuoteExceedsMonthlyPaymentCaps',
  allStipulationsCleared = 'allStipulationsCleared',
  applicationStatus = 'applicationStatus',
  autoPayDisabled = 'autoPayDisabled',
  autoPayEnabled = 'autoPayEnabled',
  bankOfAmerica = 'bankOfAmerica',
  cedEvent = 'cedEvent',
  changeOrderAccepted = 'changeOrderAccepted',
  changeOrderCancelled = 'changeOrderCancelled',
  changeOrderCreated = 'changeOrderCreated',
  changeOrderDeclined = 'changeOrderDeclined',
  changeOrderExecuted = 'changeOrderExecuted',
  changeOrderViewed = 'changeOrderViewed',
  changeOrderVoided = 'changeOrderVoided',
  concordIrregularStandingChanged = 'concordIrregularStandingChanged',
  congaActionNotifyEvent = 'congaActionNotifyEvent',
  congaMergeDocumentNotifyEvent = 'congaMergeDocumentNotifyEvent',
  consumerTaskCreated = 'consumerTaskCreated',
  consumerTaskOutstanding = 'consumerTaskOutstanding',
  consumerTaskUpdated = 'consumerTaskUpdated',
  contractApproved = 'contractApproved',
  contractDocument = 'contractDocument',
  contractEnvelopeSent = 'contractEnvelopeSent',
  contractSending = 'contractSending',
  contractSent = 'contractSent',
  contractSigned = 'contractSigned',
  contractSignerSigned = 'contractSignerSigned',
  contractVoided = 'contractVoided',
  contractReinstated = 'contractReinstated',
  delinquencyCleared = 'delinquencyCleared',
  delinquencyEntered = 'delinquencyEntered',
  directPayEvent = 'directPayEvent',
  documentStatus = 'documentStatus',
  documentUploaded = 'documentUploaded',
  firstPaymentDueDate = 'firstPaymentDueDate',
  illinoisShineDisclosureSent = 'illinoisShineDisclosureSent',
  illinoisShineDisclosureSigned = 'illinoisShineDisclosureSigned',
  leadAccepted = 'leadAccepted',
  leadRejected = 'leadRejected',
  maintenanceContractStarted = 'maintenanceContractStarted',
  milestoneAchieved = 'milestoneAchieved',
  milestonePackage = 'milestonePackage',
  milestoneStatusChanged = 'milestoneStatusChanged',
  operationAtAchieved = 'operationAtAchieved',
  payeeCreated = 'payeeCreated',
  paymentFailed = 'paymentFailed',
  placedInServiceAchieved = 'placedInServiceAchieved',
  quoteCreated = 'quoteCreated',
  quoteVoided = 'quoteVoided',
  recheckEvent = 'recheckEvent',
  requirementCompleted = 'requirementCompleted',
  requirementStatusChanged = 'requirementStatusChanged',
  sendInvite = 'sendInvite',
  stipulationAdded = 'stipulationAdded',
  stipulationCleared = 'stipulationCleared',
  stripeEvent = 'stripeEvent',
  termsAndConditionsAccepted = 'termsAndConditionsAccepted',
  trancheAssigned = 'trancheAssigned',
  trancheUnassigned = 'trancheUnassigned',
  trancheClosed = 'trancheClosed',
}

/** These are aggregate event types that group multiple related webhook events together.
 * @see getEquivalentWebhookSubscriptionTypes
 */
export enum AggregateWebhookEventType {
  allChangeOrderEvents = 'allChangeOrderEvents',
  allConsumerTaskEvents = 'allConsumerTaskEvents',
  allContractEvents = 'allContractEvents',
  allEvents = 'allEvents',
  allStipulationEvents = 'allStipulationEvents',
}

export type WebhookEventTypeOrAggregate = WebhookEventType | AggregateWebhookEventType;

export const InternalOnlyWebhooks: WebhookEventTypeOrAggregate[] = [
  WebhookEventType.concordIrregularStandingChanged,
  WebhookEventType.trancheAssigned,
  WebhookEventType.trancheUnassigned,
  WebhookEventType.trancheClosed,
  WebhookEventType.operationAtAchieved,
  WebhookEventType.placedInServiceAchieved,
  WebhookEventType.firstPaymentDueDate,
  WebhookEventType.payeeCreated,
  WebhookEventType.maintenanceContractStarted,
  WebhookEventType.autoPayEnabled,
  WebhookEventType.autoPayDisabled,
  WebhookEventType.delinquencyEntered,
  WebhookEventType.delinquencyCleared,
];

export const OrganizationWebhookEvents: WebhookEventTypeOrAggregate[] = [WebhookEventType.payeeCreated];

export enum WebhookRouteType {
  inbound = 'inbound',
  outbound = 'outbound',
}

export enum WebhookState {
  created = 'created',
  pending = 'pending',
  processing = 'processing',
  complete = 'complete',
  ignored = 'ignored',
}

export enum WebhookStatus {
  failed = 'failed',
  retrying = 'retrying',
  skipped = 'skipped',
  succeeded = 'succeeded',
}

export enum WebhookHttpMethod {
  PUT = 'PUT',
  POST = 'POST',
}

export enum NonRetryableErrorCodes {
  unAuthorized = 401,
  notFound = 404,
}

export enum WebhookAuthenticationType {
  /** Uses Palmetto provided API key. The default. */
  apiKey = 'apiKey',
  /** Uses separate client ID and client secret headers. */
  clientHeaders = 'clientHeaders',
  /** Authorization header with combined and encoded client ID, client secret (HTTP Basic authentication). */
  basicAuthHeader = 'basicAuthHeader',
}

export interface WebhookAuthenticationApiKey {
  type: WebhookAuthenticationType.apiKey;
  apiKey: string;
}

export interface WebhookAuthenticationClientHeaders {
  type: WebhookAuthenticationType.clientHeaders;
  clientId: string;
  clientSecret: string;
}

export interface WebhookAuthenticationBasicAuthHeader {
  type: WebhookAuthenticationType.basicAuthHeader;
  clientId: string;
  clientSecret: string;
}

export type WebhookAuthentication =
  | WebhookAuthenticationApiKey
  | WebhookAuthenticationClientHeaders
  | WebhookAuthenticationBasicAuthHeader;

export interface WebhookProcessingResult {
  state: WebhookState;
  status: WebhookStatus;
  errorMessage?: string;
}

export interface WebhookLog {
  data: any;
  errorMessage?: string;
  event: WebhookEventType | AggregateWebhookEventType;
  inboundEvent?: string;
  state: WebhookState;
  status?: WebhookStatus; // optional — absent until first attempt completes
  type: WebhookRouteType;
}

export interface WebhookAttempt {
  attemptNumber: number;
  processedAt: Date;
  completedAt: Date;
  status: WebhookStatus.failed | WebhookStatus.retrying;
  errorMessage?: string;
}

export interface OutboundWebhookLog extends WebhookLog {
  clientId: ObjectId;
  subscriptionId: ObjectId;
  queuedAt: Date;
  processedAt?: Date;
  completedAt?: Date;
  history?: WebhookAttempt[];
}

export interface BaseWebhookPayload {
  accountId: string;
  accountReference: string;
  event: WebhookEventTypeOrAggregate;
  organizationId?: string;
}

export interface AccountCreatedPayload extends BaseWebhookPayload {
  programType?: ProgramType;
}

export interface AccountUpdatedPayload extends BaseWebhookPayload {
  updates: Record<string, any>;
  primaryApplicantEmail?: string;
}
export interface ApplicationStatusPayload extends BaseWebhookPayload {
  applicants: ApplicationApplicant[];
  applicationId: string;
  applicationReference?: string;
  status?: FinancingDecision;
  statusDecisionDate?: Date;
  monthlyPaymentCap?: { zeroEscalationRate: number; greaterThanZeroEscalationRate: number };
}

export interface ConsumerTaskPayload
  extends
    BaseWebhookPayload,
    Pick<
      TaskDocument,
      'activityType' | 'completedAt' | 'description' | 'name' | 'startedAt' | 'status' | 'type' | 'rejectionReasons'
    > {
  applicationId: string;
  taskId: string;
  programType?: ProgramType;
  url?: string;
}

export interface ContractApprovedPayload extends BaseWebhookPayload {
  contractReference?: string;
  quoteReference?: string;
}

export interface ContractSendingPayload extends BaseWebhookPayload {
  contractReference?: string;
  quoteReference?: string;
}

export interface ContractSentPayload extends BaseWebhookPayload {
  contractReference?: string;
  quoteReference?: string;
  documentsSent: string[];
}

export type PartnerSignerRole = 'primaryApplicant' | 'coApplicant' | 'salesRep' | 'licensedInstaller';

/** Provider-neutral signing progress for one signer. Never carries names or emails. */
export interface ContractSignerSummary {
  role: PartnerSignerRole;
  status: 'signed' | 'pending';
  signedAt: string | null;
}

export interface ContractEnvelopeSentPayload extends BaseWebhookPayload {
  contractId: string;
  contractReference?: string;
  quoteReference?: string;
}

export interface ContractSignerSignedPayload extends BaseWebhookPayload {
  contractId: string;
  contractReference?: string;
  quoteReference?: string;
  signer: { role: PartnerSignerRole; signedAt: string };
  signers: ContractSignerSummary[];
}

export interface ContractSigned extends BaseWebhookPayload {
  contractReference?: string;
  quoteReference?: string;
  /** Optional so the Illinois Shine disclosure payloads that alias this type stay valid. */
  signers?: ContractSignerSummary[];
}

export type IllinoisShineDisclosureSigned = ContractSigned;
export type IllinoisShineDisclosureSent = ContractSigned;

export interface DocumentUpdatePayload extends BaseWebhookPayload {
  archived: boolean;
  documentType: AllDocumentTypes;
  documentStatus: keyof typeof DocumentStatus;
  fileNames: string[];
}

export interface DocumentUploadedPayload extends BaseWebhookPayload {
  documentId: string;
  documentType: AllDocumentTypes;
  fileNames: string[];
}

export interface QuoteContractPayload extends BaseWebhookPayload {
  contractId?: string;
  contractReference?: string;
  quoteId?: string;
  quoteReference?: string;
  message: string;
  status: ContractStatus | QuoteStatus;
}

export interface StipulationClearedPayload extends BaseWebhookPayload {
  event: WebhookEventType.stipulationCleared | WebhookEventType.allStipulationsCleared;
  applicationId: string;
  applicationReference?: string;
  stipulationType?: StipulationType | AccountStipulationType;
}

export interface StipulationAddedPayload extends BaseWebhookPayload {
  event: WebhookEventType.stipulationAdded;
  stipulationId: string;
  stipulationType: StipulationType | AccountStipulationType;
  stipulationReason?: AccountStipulationReason;
  stipulationDescription?: string;
}

export interface MilestoneChangeWebhook extends BaseWebhookPayload {
  newMilestone: MilestoneType;
  previousMilestone?: MilestoneType;
}

export interface MilestoneStatusChangedPayload extends BaseWebhookPayload {
  milestoneType: MilestoneType;
  newStatus: AccountMilestoneStatus;
  previousStatus?: AccountMilestoneStatus;
}

export interface RequirementCompletedPayload extends BaseWebhookPayload {
  requirementType: MilestoneRequirement;
  completedAt: string;
}

export interface RequirementStatusChangedPayload extends BaseWebhookPayload {
  requirementStatusUpdates: {
    requirementType: MilestoneRequirement;
    newStatus: AccountRequirementStatus;
    previousStatus?: AccountRequirementStatus;
  }[];
}

export interface TermsAndConditionsWebhookPayload extends BaseWebhookPayload {
  doeReferenceId: string;
  dateAccepted: string;
}

export interface ActiveQuoteExceedsMonthlyPaymentCapsPayload extends BaseWebhookPayload {
  monthlyPaymentCap: Pick<ApplicationMonthlyPaymentCap, 'zeroEscalationRate' | 'greaterThanZeroEscalationRate'>;
  activeQuoteId: string;
}

// organization level webhook
export interface PayeeCreatedPayload {
  event: WebhookEventType.payeeCreated;
  newPayee: string;
  programTypes: ProgramType[];
  organizationId: string;
}

export interface PaymentFailedPayload {
  accountId: string;
  organizationId?: string;
  event: WebhookEventType.paymentFailed;
  type: PAYMENT_TYPE;
  paymentMethod: PaymentMethod;
  date: string;
  amount: number;
}

export interface TrancheAssignedPayload {
  event: WebhookEventType.trancheAssigned;
  fund: {
    fundKey: string;
    fundNetSuiteSubsidiaryId: number;
    fundDescription: string;
    fundType: FundType;
  };
  tranche: {
    trancheName: string;
    trancheStatus: TrancheStatus;
    submittedDate?: Date;
    trancheDate?: Date;
    trancheDataDownloadDate?: Date;
  };
  organizationId?: string;
  accountIds: string[];
}

export interface TrancheUnassignedPayload {
  event: WebhookEventType.trancheUnassigned;
  fund: {
    fundKey: string;
    fundNetSuiteSubsidiaryId: number;
    fundDescription: string;
    fundType: FundType;
  };
  tranche: {
    trancheName: string;
    trancheStatus: TrancheStatus;
    submittedDate?: Date;
    trancheDate?: Date;
    trancheDataDownloadDate?: Date;
  };
  organizationId?: string;
  accountIds: string[];
}

export interface TrancheClosedPayload {
  event: WebhookEventType.trancheClosed;
  fund: {
    fundKey: string;
    fundNetSuiteSubsidiaryId: number;
    fundDescription: string;
    fundType: FundType;
  };
  tranche: {
    trancheName: string;
    trancheStatus: TrancheStatus.closed;
    submittedDate?: Date;
    trancheDate?: Date;
    trancheDataDownloadDate?: Date;
  };
  organizationId?: string;
  accountIds: string[];
}

export interface OperationAtAchievedPayload extends BaseWebhookPayload {
  event: WebhookEventType.operationAtAchieved;
  operationAtDate: Date;
  verificationDate: Date;
  verificationDaysTrigger: VerificationDaysTrigger;
  programType: ProgramType;
}

export interface PlacedInServiceAchievedPayload extends BaseWebhookPayload {
  event: WebhookEventType.placedInServiceAchieved;
  placedInServiceDate: Date;
  placedInServiceTrigger: PlacedInServiceTrigger;
  programType: ProgramType;
}

export interface FirstPaymentDueDateWebhookPayload extends BaseWebhookPayload {
  event: WebhookEventType.firstPaymentDueDate;
  firstPaymentDueDate: string; // MM-dd-yyyy
  programType: ProgramType;
}

export interface LeadAcceptedPayload extends Omit<BaseWebhookPayload, 'organizationId'> {
  event: WebhookEventType.leadAccepted;
  organizationId: string;
  programType: ProgramType;
}

export interface LeadRejectedPayload extends Omit<BaseWebhookPayload, 'organizationId'> {
  event: WebhookEventType.leadRejected;
  organizationId: string;
  programType: ProgramType;
}

export type ChangeOrderWebhookEventType =
  | WebhookEventType.changeOrderCreated
  | WebhookEventType.changeOrderViewed
  | WebhookEventType.changeOrderAccepted
  | WebhookEventType.changeOrderDeclined
  | WebhookEventType.changeOrderExecuted
  | WebhookEventType.changeOrderCancelled
  | WebhookEventType.changeOrderVoided;

/** Per-event detail. Every field is optional because each lifecycle event populates only the subset
 * that is meaningful for it; `status` is always present. */
export interface ChangeOrderWebhookData {
  /** The CO's status at the moment the event fired. */
  status: ChangeOrderStatus;
  /** The status the CO moved away from, where the transition is a reversal or unwind. */
  priorStatus?: ChangeOrderStatus;
  /** Why the initiating EPC opened the CO, from the canonical list. Creation-side. */
  reasons?: ChangeOrderReasonCode[];
  /** Free-text note explaining a cancellation or void — operator-supplied on the cancel route, or the
   * fixed cascade reason when a contract void unwound the CO. Unrelated to `reasons`. */
  reasonNote?: string;
  changedFields?: ChangeOrderChangedField[];
  acceptingEmail?: string;
  acceptedAt?: IsoDateTimeString;
  decliningEmail?: string;
  declinedAt?: IsoDateTimeString;
  declineReason?: string;
  /** The sub-quote this CO proposed. Present on every event that moves that quote's status: it becomes
   * the active terms on `changeOrderExecuted`, and is voided on `changeOrderCancelled` (never-executed
   * sub-quote) and on `changeOrderVoided` (only when it was the contract's active terms — compare with
   * `contractQuoteId`). */
  proposedQuoteId?: string;
  originalContractId?: string;
  /** The contract's active-terms quote pointer for this event — the original signed quote, or the
   * highest-sequence executed CO's sub-quote. Needed because a contract can have several executed COs
   * and a void/reinstate emits one event per CO: only the CO where `proposedQuoteId === contractQuoteId`
   * owns the quote that was actually deactivated (void) or reactivated (reinstate); the earlier COs'
   * sub-quotes stay `amended` throughout. */
  contractQuoteId?: string;
}

export interface ChangeOrderWebhookPayload extends BaseWebhookPayload {
  event: ChangeOrderWebhookEventType;
  /** Stable across retries and replays so a consumer can dedupe on it. Composed from the change
   * order id, the event, and — for per-signer events — the signer and that signer's timestamp. */
  id: string;
  createdAt: IsoDateTimeString;
  changeOrderId: string;
  sequence: number;
  initiatedBy: ChangeOrderInitiatedBy;
  data: ChangeOrderWebhookData;
}

export interface ConcordIrregularStandingAccountRef {
  finCoAccountId: string;
  concordAccountId: string;
}

export interface ConcordIrregularStandingCategoryDiff {
  added: ConcordIrregularStandingAccountRef[];
  removed: ConcordIrregularStandingAccountRef[];
}

export interface ConcordIrregularStandingChangedPayload {
  event: WebhookEventType.concordIrregularStandingChanged;
  /** Set by the dispatcher, as on every other payload; 'sabal' for internal-only events. */
  organizationId?: string;
  earlyStageCollections: ConcordIrregularStandingCategoryDiff;
  lateStageCollections: ConcordIrregularStandingCategoryDiff;
  paymentPlan: ConcordIrregularStandingCategoryDiff;
  bankruptcy: ConcordIrregularStandingCategoryDiff;
}

export interface AutoPayChangedPayload extends BaseWebhookPayload {
  event: WebhookEventType.autoPayEnabled | WebhookEventType.autoPayDisabled;
  previousPaymentMethod: CurrentPaymentMethod | null;
  currentPaymentMethod: CurrentPaymentMethod;
}

export type DelinquencyBucket = 'earlyStageCollections' | 'lateStageCollections' | 'paymentPlan' | 'bankruptcy';

export interface DelinquencyChangedPayload extends BaseWebhookPayload {
  event: WebhookEventType.delinquencyEntered | WebhookEventType.delinquencyCleared;
  concordAccountId: string;
  /** Bucket the account entered; null when it left the cohort. */
  bucket: DelinquencyBucket | null;
  asOfDate: IsoDateTimeString;
}

export type WebhookPayload =
  | BaseWebhookPayload
  | AccountUpdatedPayload
  | ApplicationStatusPayload
  | ConsumerTaskPayload
  | ContractSentPayload
  | ContractSigned
  | ContractEnvelopeSentPayload
  | ContractSignerSignedPayload
  | DocumentUpdatePayload
  | MilestoneChangeWebhook
  | RequirementCompletedPayload
  | StipulationAddedPayload
  | StipulationClearedPayload
  | QuoteContractPayload
  | TermsAndConditionsWebhookPayload
  | ActiveQuoteExceedsMonthlyPaymentCapsPayload
  | PayeeCreatedPayload
  | PaymentFailedPayload
  | MilestonePackageWebhook
  | TrancheAssignedPayload
  | TrancheClosedPayload
  | TrancheUnassignedPayload
  | OperationAtAchievedPayload
  | PlacedInServiceAchievedPayload
  | FirstPaymentDueDateWebhookPayload
  | LeadAcceptedPayload
  | LeadRejectedPayload
  | MaintenanceContractStartedPayload
  | ChangeOrderWebhookPayload
  | ConcordIrregularStandingChangedPayload
  | AutoPayChangedPayload
  | DelinquencyChangedPayload;

/** Consecutive failed delivery attempts after which a webhook subscription is auto-disabled. */
export const WEBHOOK_FAILURE_THRESHOLD = 10;

// type taken from WebhookSubscriptionSchema
export interface WebhookSubscription {
  id: ObjectId;
  eventType: WebhookEventTypeOrAggregate;
  clientId: ObjectId;
  hasExpired?: boolean;
  isEnabled?: boolean;
  disabledAt?: Date;
  disabledReason?: string;
  disabledDueToFailures?: boolean;
  endpointUrl: string;
  httpMethod: WebhookHttpMethod;
  nonretryableErrorCodes?: NonRetryableErrorCodes[];
  expirationDate?: Date;
}

export interface TestWebhookSubscription {
  httpMethod: WebhookHttpMethod;
  endpointUrl: string;
}

// wasn't able to find a type that worked for this from the schema that
// createWebhookSubscription needed so I made this one
export interface CreateWebhookSubscriptionProps {
  eventType: WebhookEventTypeOrAggregate;
  endpointUrl: string;
  httpMethod: WebhookHttpMethod;
  nonretryableErrorCodes?: NonRetryableErrorCodes[];
  expirationDate?: Date;
  isEnabled?: boolean;
}

// taken from WebhookSubscriptionPostResponseSchema
export interface WebhookSubscriptionPostResponse {
  messages: { clientId: string; eventType: string; duplicate?: boolean; message: string }[];
}

export interface WebhookClient extends Document {
  id: ObjectId;
  /** This is the alias, not an ObjectID */
  organizationId: string;
  name: string;
  hostUrl: string;
  authentication: WebhookAuthentication;
  /** @deprecated Use {@link WebhookAuthenticationApiKey} property instead. */
  apiKey?: string;
  includeVisibleOrgEvents?: boolean;
}

// taken from webhookClientProps const
export interface WebhookClientRequestBody {
  name: string;
  hostUrl: string;
  authentication?: WebhookAuthenticationType;
  clientId?: string;
  clientSecret?: string;
  includeVisibleOrgEvents?: boolean;
}

export interface ClientWebhookResponse {
  isSuccessful: boolean;
  isRetryable?: boolean;
  timestamp: Date;
  errorMessage?: string;
  isNotFound?: boolean;
}

export interface MilestonePackageWebhook extends BaseWebhookPayload {
  type: QueueType;
  status: QueueStatus;
  timestamp?: string;
  flags?: string;
}

export interface MaintenanceContractStartEvent extends Pick<
  OrganizationMaintenanceSettingsV2Value,
  'cadence' | 'maintenanceWindow'
> {
  firstMaintenanceDate: Date;
  coverageLength: {
    unit: MaintenancePeriodUnit;
    amount: number;
  };
}

export interface MaintenanceContractStartedPayload extends BaseWebhookPayload {
  maintenanceContract: MaintenanceContractStartEvent;
}
